import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { UserPlus, CreditCard, ClipboardCheck, FileText, CalendarCheck } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: <UserPlus size={22} strokeWidth={1.5} />,
    title: 'Sign Up',
    desc: 'Create a secure account for yourself or your child in under 2 minutes.',
  },
  {
    number: '02',
    icon: <CreditCard size={22} strokeWidth={1.5} />,
    title: 'Choose a Plan',
    desc: 'Select the plan that fits your goals — from single assessments to full subscriptions.',
  },
  {
    number: '03',
    icon: <ClipboardCheck size={22} strokeWidth={1.5} />,
    title: 'Take Assessment',
    desc: 'Complete structured, scientifically-designed assessments at your own pace.',
  },
  {
    number: '04',
    icon: <FileText size={22} strokeWidth={1.5} />,
    title: 'Receive AI Report',
    desc: 'Get a detailed, AI-interpreted advisory report in clear, parent-friendly language.',
  },
  {
    number: '05',
    icon: <CalendarCheck size={22} strokeWidth={1.5} />,
    title: 'Book Counseling',
    desc: 'Book a session with an expert counselor for personalized guidance and support.',
  },
];

const HowItWorks = ({ onOpenAssessment }) => {
  const [ref, visible] = useScrollReveal();
  const handleStart = () => {
    onOpenAssessment?.();
  };

  return (
    <section
      id="how-it-works"
      ref={ref}
      style={{ padding: '100px 24px', background: 'linear-gradient(180deg, rgba(240,244,242,0.6) 0%, #F9F9F7 100%)' }}
      data-testid="how-it-works-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '72px' }}>
          <div className="section-label">The Journey</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            How MindVrik <span style={{ color: '#2A9D8F', fontWeight: 300 }}>Works</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '460px', margin: '0 auto', lineHeight: 1.7 }}>
            A simple, guided five-step journey from registration to expert counseling.
          </p>
        </div>

        {/* Steps grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', position: 'relative' }}>
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
              data-testid={`step-${i + 1}`}
              style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
            >
              {/* Connector line (except last) */}
              {i < steps.length - 1 && (
                <div style={{
                  display: 'none',
                  position: 'absolute',
                  top: '32px',
                  left: '60%',
                  right: '-40%',
                  height: '1px',
                  background: 'linear-gradient(90deg, rgba(42,157,143,0.35), rgba(42,157,143,0.08))',
                  zIndex: 0,
                }} className="step-connector-hidden" />
              )}

              {/* Step card */}
              <div
                className="card-lift"
                style={{
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(42,157,143,0.12)',
                  borderRadius: '24px',
                  padding: '32px 24px',
                  width: '100%',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }}
              >
                {/* Number */}
                <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 800, letterSpacing: '0.1em', color: 'rgba(42,157,143,0.4)', marginBottom: '16px' }}>
                  STEP {step.number}
                </div>
                {/* Icon circle */}
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(42,157,143,0.12), rgba(42,157,143,0.06))',
                  border: '1.5px solid rgba(42,157,143,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#2A9D8F', margin: '0 auto 20px',
                }}>{step.icon}</div>
                {/* Title */}
                <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '18px', color: '#1A202C', margin: '0 0 10px' }}>
                  {step.title}
                </h3>
                {/* Desc */}
                <p style={{ fontFamily: 'Inter', fontSize: '13px', color: '#64748B', lineHeight: 1.65, margin: 0 }}>
                  {step.desc}
                </p>
              </div>

              {/* Arrow connector for horizontal */}
              {i < steps.length - 1 && (
                <div style={{
                  position: 'absolute',
                  right: '-13px',
                  top: '45px',
                  width: '24px',
                  height: '24px',
                  borderTop: '1.5px solid rgba(42,157,143,0.3)',
                  borderRight: '1.5px solid rgba(42,157,143,0.3)',
                  transform: 'rotate(45deg)',
                  zIndex: 1,
                  display: 'none',
                }} className="step-arrow" />
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`reveal reveal-delay-6 ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginTop: '56px' }}>
          <button className="btn-primary" data-testid="how-it-works-cta" onClick={handleStart}>
            Start Your Journey
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
